import { TriangleAlert } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { AlertDialog, Button, Modal } from '../../components/ui';
import { LABEL, T } from './texts';

/*
 * Окно с введённым (004, contracts/ui.md, «Как окна открываются»): Esc, крестик и «Отмена» закрывают окно сразу, если
 * в нём ничего не введено; иначе система сначала спрашивает «Закрыть окно?». Содержимое живёт, пока окно открыто: при
 * новом открытии введённое начинается с начала. Окна возврата, «Не вводить», примера, каталога и просьб.
 */

/** Что окно даёт содержимому: отметить введённое и закрыть (с вопросом, если введено). */
export interface ModalControl {
  readonly setDirty: (dirty: boolean) => void;
  readonly requestClose: () => void;
}

/** Введённое в окне: при `dirty` закрыть окно можно только через вопрос. */
export function useDirty(ctl: ModalControl, dirty: boolean): void {
  const { setDirty } = ctl;
  useEffect(() => setDirty(dirty), [dirty, setDirty]);
}

export function GuardedModal({
  open,
  onClose,
  title,
  description,
  width,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  width?: string;
  children: (ctl: ModalControl) => ReactNode;
}) {
  const dirty = useRef(false);
  const [asking, setAsking] = useState(false);
  useEffect(() => {
    if (!open) {
      dirty.current = false;
      setAsking(false);
    }
  }, [open]);
  const latest = useRef(onClose);
  latest.current = onClose;
  const setDirty = useCallback((d: boolean) => {
    dirty.current = d;
  }, []);
  const requestClose = useCallback(() => {
    if (dirty.current) setAsking(true);
    else latest.current();
  }, []);
  const ctl = useRef<ModalControl>({ setDirty, requestClose }).current;
  return (
    <>
      <Modal open={open} onClose={requestClose} title={title} description={description} width={width}>
        {children(ctl)}
      </Modal>
      <AlertDialog
        open={asking}
        onClose={() => setAsking(false)}
        title={LABEL.closeAskAny}
        icon={TriangleAlert}
        tone="warn"
        description={T.closeAskText}
      >
        <Button data-autofocus onClick={() => setAsking(false)}>
          {LABEL.backToDialog}
        </Button>
        <Button
          variant="danger"
          onClick={() => {
            setAsking(false);
            dirty.current = false;
            latest.current();
          }}
        >
          {LABEL.closeDialog}
        </Button>
      </AlertDialog>
    </>
  );
}

/** Подвал окна: причина недоступности главной кнопки — слева, рядом с кнопкой (FR-037); на телефоне — над кнопками. */
export function DialogFooter({
  reasonId,
  reason,
  children,
}: {
  reasonId: string;
  reason: string | null;
  children: ReactNode;
}) {
  return (
    <div
      data-footer
      className="sticky bottom-0 -mx-6 -mb-6 mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface px-6 py-3 max-sm:-mx-4 max-sm:-mb-5 max-sm:px-4 max-sm:pb-[max(12px,env(safe-area-inset-bottom))] max-sm:[&>button]:flex-auto"
    >
      {reason && (
        <p id={reasonId} className="mr-auto text-[12.5px] text-ink-2 max-sm:w-full">
          {reason}
        </p>
      )}
      {children}
    </div>
  );
}
